import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import { useZones } from '@/api/queries'
import { getViewAs } from '@/staff/view-as'

import { ZoneContext, type ZoneContextValue } from './zone-context'

const STORAGE_KEY = 'saltseed.active-zone'

function readStoredZoneId(): string | null {
  // Viewing another account: its zones are not the staff member's own, so the stored choice is
  // ignored (and, below, never overwritten). A break-glass grant names the zone to open.
  const viewing = getViewAs()
  if (viewing !== null) return viewing.breakglass?.zoneId ?? null
  try {
    return window.localStorage.getItem(STORAGE_KEY)
  } catch {
    // Private mode, blocked storage — the console still works, it just forgets.
    return null
  }
}

function writeStoredZoneId(zoneId: string | null): void {
  if (getViewAs() !== null) return
  try {
    if (zoneId === null) window.localStorage.removeItem(STORAGE_KEY)
    else window.localStorage.setItem(STORAGE_KEY, zoneId)
  } catch {
    /* ignore */
  }
}

/**
 * Owns the active-zone selection for the whole console.
 *
 * The persisted id is a *request*, not a guarantee: a USER's grant can be
 * revoked between two sessions, and §18 answers an inaccessible zone with 404
 * rather than 403. So the request is always reconciled against the zone list
 * the server actually returns, and a dropped choice raises a dismissible notice
 * instead of leaving every page 404ing.
 *
 * The list is `state=all`, **including archived zones**. §17 keeps an archived
 * zone's history readable *forever* and that is the whole reason there is no
 * DELETE; loading only `active` would make every archived zone's messages
 * unreachable from the console, which quietly contradicts the contract. Nothing
 * else has to change for that to be safe: every write is already gated on
 * `zone.reachable`, which the server sets false for an archived zone, so those
 * pages degrade to read-only on their own.
 *
 * An archived zone is selectable but never *selected by default* — falling into
 * a retired zone on first sign-in would be a bizarre landing spot.
 */
export function ZoneProvider({ children }: { children: ReactNode }) {
  const zonesQuery = useZones('all')
  const [requestedZoneId, setRequestedZoneId] = useState<string | null>(() => readStoredZoneId())
  const [lostPersistedZone, setLostPersistedZone] = useState(false)

  const zones = useMemo(() => zonesQuery.data ?? [], [zonesQuery.data])

  const activeZone = useMemo(() => {
    if (zones.length === 0) return undefined
    const requested =
      requestedZoneId === null ? undefined : zones.find((zone) => zone.id === requestedZoneId)
    // An explicit choice is honoured whatever its state; the fallback prefers a
    // zone that still works.
    return requested ?? zones.find((zone) => zone.zone_state !== 'ARCHIVED') ?? zones[0]
  }, [zones, requestedZoneId])

  // Reconcile a stale persisted choice once the real list has arrived.
  useEffect(() => {
    if (!zonesQuery.isSuccess || requestedZoneId === null) return
    if (zones.some((zone) => zone.id === requestedZoneId)) return
    setLostPersistedZone(true)
    setRequestedZoneId(
      zones.find((zone) => zone.zone_state !== 'ARCHIVED')?.id ?? zones[0]?.id ?? null,
    )
  }, [zonesQuery.isSuccess, requestedZoneId, zones])

  useEffect(() => {
    writeStoredZoneId(activeZone?.id ?? null)
  }, [activeZone])

  const selectZone = useCallback((zoneId: string) => {
    setLostPersistedZone(false)
    setRequestedZoneId(zoneId)
  }, [])

  const dismissLostZone = useCallback(() => {
    setLostPersistedZone(false)
  }, [])

  const { refetch } = zonesQuery
  const refresh = useCallback(() => {
    void refetch()
  }, [refetch])

  const value = useMemo<ZoneContextValue>(
    () => ({
      zones,
      activeZone,
      activeZoneId: activeZone?.id,
      selectZone,
      isLoading: zonesQuery.isLoading,
      isError: zonesQuery.isError,
      error: zonesQuery.error,
      refetch: refresh,
      lostPersistedZone,
      dismissLostZone,
    }),
    [
      zones,
      activeZone,
      selectZone,
      zonesQuery.isLoading,
      zonesQuery.isError,
      zonesQuery.error,
      refresh,
      lostPersistedZone,
      dismissLostZone,
    ],
  )

  return <ZoneContext.Provider value={value}>{children}</ZoneContext.Provider>
}
